// /admin/guias-de-tamanho — Guia de tamanhos (ADR-014, docs/specs/2026-09-23-
// guia-de-tamanhos-design.md secao 6). Molde estrutural e visual de
// AdminCareInstructionsPage.tsx/AdminTechnicalitiesPage.tsx (PageHeader +
// lista com drag de ordem + Dialog + toast + invalidateQueries), com duas
// diferencas de fundo: (1) topo fixo "Como tirar medidas" (bloco global,
// nao um item da lista); (2) o editor tem editor de TABELA (colunas x
// linhas) + categorias vinculadas, porque o guia e 0..1 por produto/
// categoria — nao uma lista de chips como tecnicidade/lavagem.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, RefreshCw, Ruler, Trash2, Upload } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, apiErrorMessage } from "../../lib/api";
import type {
  Category,
  RepublishSizeGuideResponse,
  SizeGuideAdmin,
  SizeGuideCategoriesMoved,
  SizeGuideHowToMeasure,
  SizeGuideRow,
} from "../../lib/types";
import { emptySizeGuideRow, validateSizeGuideTable } from "../../lib/size-guide";
import { PageHeader } from "../../components/layout/PageHeader";
import { SizeGuideTableEditor } from "../../components/SizeGuideTableEditor";
import { MultiCategoryPicker } from "../../components/MultiCategoryPicker";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";

const QUERY_KEY = ["admin-size-guides"];
const CATEGORIES_QUERY_KEY = ["categories"];

function flattenCategories(cats: Category[], prefix = ""): { id: number; label: string }[] {
  const out: { id: number; label: string }[] = [];
  for (const c of cats) {
    const label = prefix ? `${prefix} › ${c.name}` : c.name;
    out.push({ id: c.id, label });
    if (c.children && c.children.length > 0) out.push(...flattenCategories(c.children, label));
  }
  return out;
}

export function AdminSizeGuidesPage(): JSX.Element {
  const qc = useQueryClient();
  const {
    data: items = [],
    isLoading,
    isError,
    error,
  } = useQuery<SizeGuideAdmin[]>({
    queryKey: QUERY_KEY,
    queryFn: async () =>
      (await api.get<{ items: SizeGuideAdmin[] }>("/api/admin/size-guides")).data.items,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: async () => (await api.get<Category[]>("/api/categories")).data,
  });
  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SizeGuideAdmin | null>(null);

  const [order, setOrder] = useState<number[]>([]);
  const idsKey = items.map((i) => i.id).join(",");
  const [syncedKey, setSyncedKey] = useState("");
  if (syncedKey !== idsKey) {
    const incoming = items.map((i) => i.id);
    const incomingSet = new Set(incoming);
    const preserved = order.filter((id) => incomingSet.has(id));
    const fresh = incoming.filter((id) => !preserved.includes(id));
    setOrder([...preserved, ...fresh]);
    setSyncedKey(idsKey);
  }

  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const orderMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await api.put("/api/admin/size-guides/order", { ids });
    },
    onSuccess: () => {
      toast.success("Ordem salva");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const onDragEnd = (e: DragEndEvent): void => {
    if (!e.over || e.over.id === e.active.id) return;
    const activeId = Number(e.active.id);
    const overId = Number(e.over.id);
    setOrder((curr) => {
      const oldIdx = curr.indexOf(activeId);
      const newIdx = curr.indexOf(overId);
      if (oldIdx < 0 || newIdx < 0) return curr;
      const next = arrayMove(curr, oldIdx, newIdx);
      orderMutation.mutate(next);
      return next;
    });
  };

  const delMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/admin/size-guides/${id}`);
    },
    onSuccess: () => {
      toast.success("Guia de tamanhos excluído");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      if ((err as { response?: { status?: number } })?.response?.status === 409) {
        toast.error("Este guia está vinculado a categorias ou produtos — desative em vez de excluir, ou desvincule antes.");
        return;
      }
      toast.error(apiErrorMessage(err));
    },
  });

  const republishMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<RepublishSizeGuideResponse>(`/api/admin/size-guides/${id}/republish`)).data,
    onSuccess: (d) => {
      toast.success(
        `${d.enqueued} publicação${d.enqueued === 1 ? "" : "ões"} enfileirada${d.enqueued === 1 ? "" : "s"}` +
          (d.skipped_already_queued > 0 ? ` (${d.skipped_already_queued} já estavam na fila)` : ""),
      );
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div>
      <PageHeader
        title="Guias de tamanho"
        description="Tabela de medidas por modelagem + foto de medição, vinculados por categoria (com exceção por produto)."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="icon" aria-hidden /> Novo guia de tamanhos
          </Button>
        }
      />

      <div className="stack" style={{ gap: "var(--sp-4)" }}>
        <HowToMeasureCard />

        {isLoading ? (
          <div className="card stack">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError ? (
          <EmptyState title="Erro" description={apiErrorMessage(error)} />
        ) : items.length === 0 ? (
          <EmptyState
            title="Nenhum guia de tamanhos cadastrado"
            description="Cadastre o primeiro (ex.: Camisetas femininas, Tops)."
            action={<Button onClick={() => setCreateOpen(true)}>Criar primeiro guia de tamanhos</Button>}
          />
        ) : (
          <div className="card" style={{ padding: "0 1rem" }}>
            <div
              className="row row__head hidden md:grid"
              style={{
                gridTemplateColumns:
                  "1.25rem minmax(0,3fr) minmax(4rem,1.5fr) minmax(4rem,1.5fr) minmax(0,3fr) minmax(3.5rem,1.5fr) minmax(5rem,1.5fr) minmax(8rem,2fr)",
              }}
            >
              <span />
              <span>Título</span>
              <span>Colunas</span>
              <span>Tamanhos</span>
              <span>Categorias</span>
              <span>Ativo</span>
              <span>Produtos</span>
              <span style={{ textAlign: "right" }}>Ações</span>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={order} strategy={verticalListSortingStrategy}>
                {order.map((id) => {
                  const g = byId.get(id);
                  if (!g) return null;
                  return (
                    <SizeGuideRowItem
                      key={id}
                      item={g}
                      flatCategories={flatCategories}
                      onEdit={() => setEditing(g)}
                      onDelete={() => {
                        if (confirm(`Excluir o guia de tamanhos "${g.title}"?`)) delMut.mutate(g.id);
                      }}
                      onRepublish={() => {
                        if (
                          confirm(
                            `Enfileirar republish de ${g.n_products_effective} produto${g.n_products_effective === 1 ? "" : "s"}?`,
                          )
                        ) {
                          republishMut.mutate(g.id);
                        }
                      }}
                      deleting={delMut.isPending}
                      republishing={republishMut.isPending}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      <SizeGuideDialog
        key={editing?.id ?? "new"}
        open={createOpen || editing !== null}
        editing={editing}
        allGuides={items}
        flatCategories={flatCategories}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function SizeGuideRowItem({
  item,
  flatCategories,
  onEdit,
  onDelete,
  onRepublish,
  deleting,
  republishing,
}: {
  item: SizeGuideAdmin;
  flatCategories: { id: number; label: string }[];
  onEdit: () => void;
  onDelete: () => void;
  onRepublish: () => void;
  deleting: boolean;
  republishing: boolean;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const catLabels = item.category_ids
    .map((id) => flatCategories.find((c) => c.id === id)?.label)
    .filter((x): x is string => Boolean(x));
  const handle = (
    <span
      {...attributes}
      {...listeners}
      style={{ color: "var(--color-ink-3)", cursor: "grab", fontSize: ".875rem", textAlign: "center" }}
    >
      ⠿
    </span>
  );
  const actions = (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRepublish}
        disabled={republishing || item.n_products_effective === 0}
        title={
          item.n_products_effective === 0
            ? "Nenhum produto usa este guia ainda"
            : `Republicar os ${item.n_products_effective} produtos que usam este guia`
        }
      >
        <RefreshCw className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
      </Button>
      <Button variant="ghost" size="sm" onClick={onEdit}>
        Editar
      </Button>
      <Button variant="ghost" size="sm" onClick={onDelete} disabled={deleting}>
        <Trash2 className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
      </Button>
    </>
  );

  // Abaixo de 48rem (`md`, mesmo corte do DataTable) a linha de 8 colunas
  // nao cabe: em 375px a grade media 498px e a tela rolava de lado (achado
  // 04/11/2026). Vira .row-card, como as outras listas do admin.
  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="row hidden md:grid"
        style={{
          gridTemplateColumns:
            "1.25rem minmax(0,3fr) minmax(4rem,1.5fr) minmax(4rem,1.5fr) minmax(0,3fr) minmax(3.5rem,1.5fr) minmax(5rem,1.5fr) minmax(8rem,2fr)",
          padding: "0",
        }}
      >
      {handle}
      <span className="row__cell" style={{ fontWeight: 900, whiteSpace: "normal" }}>{item.title}</span>
      <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
        {item.columns.length}
      </span>
      <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
        {item.rows.length}
      </span>
      <span className="row__cell truncate" style={{ color: "var(--color-ink-2)" }} title={catLabels.join(", ")}>
        {catLabels.length > 0 ? catLabels.join(", ") : "—"}
      </span>
      <span>
        <Badge variant={item.active ? "live" : "outline"}>{item.active ? "Ativo" : "Inativo"}</Badge>
      </span>
      <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
        {item.n_products_effective} produto{item.n_products_effective === 1 ? "" : "s"}
      </span>
      <span className="cluster" style={{ justifyContent: "flex-end" }}>{actions}</span>
      </div>
      <div className="row-card md:hidden" style={{ margin: ".75rem 0" }}>
        {handle}
        <div className="row-card__body">
          <span className="row__title">{item.title}</span>
          <div className="cluster">
            <Badge variant={item.active ? "live" : "outline"}>{item.active ? "Ativo" : "Inativo"}</Badge>
            <span className="chip chip--mono">{item.columns.length} colunas</span>
            <span className="chip chip--mono">{item.rows.length} tamanhos</span>
            <span className="chip chip--mono">
              {item.n_products_effective} produto{item.n_products_effective === 1 ? "" : "s"}
            </span>
          </div>
          <span className="field__hint" style={{ whiteSpace: "normal" }}>
            {catLabels.length > 0 ? catLabels.join(", ") : "Sem categoria"}
          </span>
          <div className="cluster">{actions}</div>
        </div>
      </div>
    </div>
  );
}

function HowToMeasureCard(): JSX.Element {
  const qc = useQueryClient();
  const QK = ["size-guide-how-to-measure"];
  const { data, isLoading } = useQuery<SizeGuideHowToMeasure>({
    queryKey: QK,
    queryFn: async () => (await api.get<SizeGuideHowToMeasure>("/api/admin/size-guides/how-to-measure")).data,
  });

  const [title, setTitle] = useState(data?.title ?? "");
  const [text, setText] = useState(data?.text ?? "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(data?.image_url ?? null);

  useEffect(() => {
    if (!data) return;
    setTitle(data.title);
    setText(data.text);
    setPreviewUrl(data.image_url);
  }, [data]);

  useEffect(() => {
    return () => {
      if (imageFile && previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.put("/api/admin/size-guides/how-to-measure", { title, text });
      if (imageFile) {
        const form = new FormData();
        form.append("image", imageFile);
        await api.post("/api/admin/size-guides/how-to-measure/image", form, {
          headers: { "Content-Type": undefined },
        });
      }
    },
    onSuccess: () => {
      toast.success('Bloco "Como tirar medidas" salvo');
      setImageFile(null);
      void qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <Card className="stack">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  return (
    <Card accentLeft="lilac">
      <div className="cluster" style={{ alignItems: "baseline", marginBottom: ".75rem" }}>
        <Ruler className="icon" aria-hidden />
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Como tirar medidas</div>
        <span className="card__meta">bloco global, aparece em todos os guias na loja</span>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="stack"
        style={{ gap: "var(--sp-3)" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <div className="stack" style={{ gap: "var(--sp-3)" }}>
            <div>
              <Label htmlFor="htm-title">Título</Label>
              <Input id="htm-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
            </div>
            <div>
              <Label htmlFor="htm-text">Texto</Label>
              <Textarea id="htm-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={4000} />
            </div>
          </div>
          <div className="stack" style={{ gap: ".5rem", alignItems: "center" }}>
            <div
              className="rounded-md border border-line"
              style={{
                width: "9rem",
                height: "9rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                background: "var(--color-soft)",
              }}
            >
              {previewUrl ? (
                <img src={previewUrl} alt="" className="h-full w-full object-contain" />
              ) : (
                <span className="field__hint">Sem imagem</span>
              )}
            </div>
            <label className="btn btn--secondary" style={{ cursor: "pointer" }}>
              <Upload className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
              {imageFile ? imageFile.name : "Trocar imagem"}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setImageFile(f);
                  if (f) setPreviewUrl(URL.createObjectURL(f));
                }}
              />
            </label>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" loading={saveMutation.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SizeGuideDialog({
  open,
  editing,
  allGuides,
  flatCategories,
  onOpenChange,
}: {
  open: boolean;
  editing: SizeGuideAdmin | null;
  allGuides: SizeGuideAdmin[];
  flatCategories: { id: number; label: string }[];
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const isEdit = editing !== null;

  const [title, setTitle] = useState(editing?.title ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [unit, setUnit] = useState(editing?.unit ?? "cm");
  const [columns, setColumns] = useState<string[]>(editing?.columns ?? ["Busto", "Cintura", "Quadril"]);
  const [rows, setRows] = useState<SizeGuideRow[]>(
    editing?.rows ?? [emptySizeGuideRow(3, "P"), emptySizeGuideRow(3, "M"), emptySizeGuideRow(3, "G")],
  );
  const [notesValue, setNotesValue] = useState(editing?.notes ?? "");
  const [active, setActive] = useState(editing?.active ?? true);
  const [categoryIds, setCategoryIds] = useState<number[]>(editing?.category_ids ?? []);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(editing?.image_url ?? null);

  useEffect(() => {
    return () => {
      if (imageFile && previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile]);

  const tableErrors = validateSizeGuideTable(columns, rows);

  // aviso "esta categoria está no guia X e será movida" — cruza a selecao
  // atual com os OUTROS guias ja carregados (RN nao precisa de round-trip:
  // so precisa da lista, que a pagina ja tem).
  const conflicts = useMemo(() => {
    const out: { categoryLabel: string; guideTitle: string }[] = [];
    for (const catId of categoryIds) {
      const owner = allGuides.find((g) => g.id !== editing?.id && g.category_ids.includes(catId));
      if (owner) {
        const label = flatCategories.find((c) => c.id === catId)?.label ?? `#${catId}`;
        out.push({ categoryLabel: label, guideTitle: owner.title });
      }
    }
    return out;
  }, [categoryIds, allGuides, editing?.id, flatCategories]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        title,
        description,
        unit,
        columns,
        rows,
        notes: notesValue,
        active,
      };
      let id = editing?.id;
      if (isEdit && id) {
        await api.put(`/api/admin/size-guides/${id}`, body);
      } else {
        const r = await api.post<{ id: number }>("/api/admin/size-guides", body);
        id = r.data.id;
      }
      if (imageFile && id) {
        const form = new FormData();
        form.append("image", imageFile);
        await api.post(`/api/admin/size-guides/${id}/image`, form, {
          headers: { "Content-Type": undefined },
        });
      }
      if (id) {
        const catsChanged =
          !isEdit ||
          categoryIds.length !== (editing?.category_ids.length ?? 0) ||
          categoryIds.some((c) => !(editing?.category_ids ?? []).includes(c));
        if (catsChanged) {
          // Contrato da API (admin/size-guides.ts): moved = [{category_id, size_guide_id}]
          // com o guia de ONDE a categoria saiu; o titulo vem da lista ja carregada.
          const r = await api.put<{ moved?: { category_id: number; size_guide_id: number }[] }>(
            `/api/admin/size-guides/${id}/categories`,
            { category_ids: categoryIds },
          );
          const guides = qc.getQueryData<SizeGuideAdmin[]>(QUERY_KEY) ?? [];
          const byGuide = new Map<number, SizeGuideCategoriesMoved>();
          for (const m of r.data.moved ?? []) {
            const g = byGuide.get(m.size_guide_id) ?? {
              size_guide_id: m.size_guide_id,
              size_guide_title: guides.find((x) => x.id === m.size_guide_id)?.title ?? `guia ${m.size_guide_id}`,
              category_ids: [],
            };
            g.category_ids.push(m.category_id);
            byGuide.set(m.size_guide_id, g);
          }
          return [...byGuide.values()];
        }
      }
      return [];
    },
    onSuccess: (moved) => {
      toast.success(isEdit ? "Guia de tamanhos atualizado" : "Guia de tamanhos criado");
      if (moved.length > 0) {
        const names = moved.map((m) => m.size_guide_title).join(", ");
        toast.info(`Categorias movidas do(s) guia(s): ${names}.`);
      }
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const canSave = title.trim().length > 0 && tableErrors.length === 0 && !saveMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={editing?.id ?? "new"}>
      <DialogContent style={{ maxWidth: "min(48rem, 92vw)" }}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar guia de tamanhos" : "Novo guia de tamanhos"}</DialogTitle>
          <DialogDescription>
            Tabela de medidas por tamanho + foto de medição. Vincule a categorias — a categoria mais
            específica do produto vence.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) saveMutation.mutate();
          }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr_6rem] gap-3">
            <div>
              <Label htmlFor="sg-title">Título</Label>
              <Input id="sg-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={120} />
            </div>
            <div>
              <Label htmlFor="sg-unit">Unidade</Label>
              <Input id="sg-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="cm" maxLength={10} />
            </div>
          </div>
          <div>
            <Label htmlFor="sg-desc">Descrição</Label>
            <Textarea id="sg-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={600} />
          </div>

          <div>
            <Label>Tabela de medidas</Label>
            <div style={{ marginTop: ".375rem" }}>
              <SizeGuideTableEditor columns={columns} rows={rows} onChange={(c, r) => { setColumns(c); setRows(r); }} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <div>
              <Label htmlFor="sg-notes">Observação</Label>
              <Textarea id="sg-notes" value={notesValue} onChange={(e) => setNotesValue(e.target.value)} rows={2} maxLength={600} />
            </div>
            <div className="stack" style={{ gap: ".5rem", alignItems: "center" }}>
              <Label>Foto de medição</Label>
              <div
                className="rounded-md border border-line"
                style={{
                  width: "7rem",
                  height: "7rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  background: "var(--color-soft)",
                }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="" className="h-full w-full object-contain" />
                ) : (
                  <span className="field__hint" style={{ fontSize: "var(--text-sm)" }}>Sem foto</span>
                )}
              </div>
              <label className="btn btn--secondary" style={{ cursor: "pointer" }}>
                <Upload className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
                {imageFile ? "Trocar" : "Enviar"}
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setImageFile(f);
                    if (f) setPreviewUrl(URL.createObjectURL(f));
                  }}
                />
              </label>
            </div>
          </div>

          <div>
            <Label>Categorias</Label>
            <div style={{ marginTop: ".375rem" }}>
              <MultiCategoryPicker categories={flatCategories} selected={categoryIds} onChange={setCategoryIds} />
            </div>
            {conflicts.length > 0 ? (
              <div
                className="stack"
                style={{
                  gap: ".25rem",
                  marginTop: ".5rem",
                  background: "var(--color-soft)",
                  borderRadius: "var(--radius-md)",
                  padding: ".5rem .625rem",
                }}
              >
                {conflicts.map((c, i) => (
                  <p key={i} className="field__hint">
                    <b className="text-ink">{c.categoryLabel}</b> está no guia <b className="text-ink">{c.guideTitle}</b> e
                    será movida para este guia ao salvar.
                  </p>
                ))}
              </div>
            ) : null}
          </div>

          <Switch checked={active} onChange={(e) => setActive(e.target.checked)} label="Ativo" />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" loading={saveMutation.isPending} disabled={!canSave}>
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
